import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SWIPE_CLOSE_PX = 80;

/** Resolves once the last overlay's own `history.back()` has landed; a new entry must wait for it. */
let pendingBack = null;

/**
 * One history entry per open overlay, so Escape and the Android / browser back button close it.
 * Stacked overlays each own an entry; only the topmost reacts to Back / Escape.
 * Returns `closeThen(fn)`: closes, then runs `fn` once the entry is gone, so navigation never gets undone.
 */
export function useBackToClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const marker = Math.random().toString(36).slice(2);
    const isTop = () => window.history.state?.__sheet === marker;
    let active = true;
    let pushed = false;
    const onPop = () => !isTop() && onCloseRef.current?.();
    const onKey = (e) => e.key === "Escape" && isTop() && onCloseRef.current?.();
    const push = () => {
      if (!active) return;
      window.history.pushState({ ...window.history.state, __sheet: marker }, "");
      pushed = true;
      window.addEventListener("popstate", onPop);
    };
    if (pendingBack) pendingBack.then(push);
    else push();
    window.addEventListener("keydown", onKey);
    return () => {
      active = false;
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
      const after = pendingRef.current;
      pendingRef.current = null;
      if (pushed && isTop()) {
        const landed = new Promise((res) => window.addEventListener("popstate", res, { once: true }));
        const done = landed.then(() => {
          if (pendingBack === done) pendingBack = null;
          after?.();
        });
        pendingBack = done;
        window.history.back();
      } else if (after) {
        after();
      }
    };
  }, [open]);

  return (fn) => {
    pendingRef.current = fn;
    onCloseRef.current?.();
  };
}

/**
 * Phone bottom sheet. Closes on backdrop tap, Escape, swipe-down on the handle and the back button.
 * `children` may be a function receiving `closeThen(fn)` (see useBackToClose).
 */
export default function MobileSheet({ open, onClose, ariaLabel, header = null, children }) {
  const closeThen = useBackToClose(open, onClose);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [dragY, setDragY] = useState(0);

  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true });
    else setDragY(0);
  }, [open]);

  if (!open) return null;

  const onPointerDown = (e) => {
    if (e.target.closest?.("button, a, input, select")) return;
    dragRef.current = { startY: e.clientY, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (dragRef.current?.id !== e.pointerId) return;
    setDragY(Math.max(0, e.clientY - dragRef.current.startY));
  };
  const onPointerEnd = (e) => {
    if (dragRef.current?.id !== e.pointerId) return;
    dragRef.current = null;
    if (dragY > SWIPE_CLOSE_PX) onClose?.();
    else setDragY(0);
  };

  return createPortal(
    <div className="fixed inset-0 z-[12000] flex items-end justify-center">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={() => onClose?.()}
        className="absolute inset-0 cursor-default bg-black/40 animate-in fade-in duration-200 motion-reduce:animate-none"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className="relative flex max-h-[85dvh] w-full flex-col rounded-t-3xl bg-[hsl(var(--ds-surface))] shadow-2xl outline-none animate-in slide-in-from-bottom duration-300 ease-out motion-reduce:animate-none"
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragRef.current ? "none" : "transform 200ms ease-out",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div
          className="shrink-0 touch-none select-none px-5 pb-2 pt-3"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          <div className="mx-auto h-1.5 w-10 rounded-full bg-[hsl(var(--ds-border))]" />
          {header}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5">
          {typeof children === "function" ? children(closeThen) : children}
        </div>
      </div>
    </div>,
    document.body
  );
}
